import React, { useState, useRef, useEffect } from 'react';
import { chatAPI, modelsAPI } from '../services/api';
import ChatMessage from './ChatMessage';
import ModelSelector from './ModelSelector';

const QUICK_ACTIONS = [
    { label: 'Listar domínios', prompt: 'Liste todos os domínios cadastrados no Cloudflare' },
    { label: 'Verificar propagação', prompt: 'Verifique a propagação DNS de todos os domínios' },
    { label: 'Buscar por IP', prompt: 'Busque todos os registros que apontam para ' },
    { label: 'Status nameservers', prompt: 'Verifique o status dos nameservers de todos os domínios' }
];

function AIChat() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModelSelector, setShowModelSelector] = useState(false);
    const [currentModel, setCurrentModel] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        modelsAPI.getCurrent().then(({ data }) => {
            if (data.success) setCurrentModel(data.model);
        }).catch(() => {});
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const sendMessage = async (text) => {
        if (!text.trim() || loading) return;

        const userMessage = { role: 'user', content: text.trim() };
        const newMessages = [...messages, userMessage];
        setMessages(newMessages);
        setInput('');
        setError(null);
        setLoading(true);

        try {
            // Send only role+content for the API
            const apiMessages = newMessages.map(m => ({
                role: m.role,
                content: m.content
            }));

            const { data } = await chatAPI.sendMessage(apiMessages);

            if (data.success) {
                const assistantMessage = {
                    role: 'assistant',
                    content: data.response,
                    toolExecutions: data.toolExecutions || []
                };
                setMessages(prev => [...prev, assistantMessage]);
            } else {
                setError(data.error || 'Erro ao processar mensagem.');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || err.message || 'Erro de conexão com o servidor.';
            setError(errorMsg);
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(input);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input);
        }
    };

    const handleQuickAction = (action) => {
        if (action.prompt.endsWith(' ')) {
            setInput(action.prompt);
            inputRef.current?.focus();
        } else {
            sendMessage(action.prompt);
        }
    };

    const clearChat = () => {
        setMessages([]);
        setError(null);
    };

    return (
        <div className="ai-chat">
            <div className="ai-chat-header">
                <div className="ai-chat-title">
                    <span className="ai-chat-icon">{'\uD83E\uDD16'}</span>
                    <div>
                        <h3>Assistente DNS</h3>
                        <p>Gerencie seus domínios com linguagem natural</p>
                    </div>
                </div>
                <div className="ai-chat-header-actions">
                    <button
                        className="btn btn-sm btn-model"
                        onClick={() => setShowModelSelector(true)}
                        title={currentModel}
                    >
                        {currentModel ? currentModel.split('/').pop() : 'Modelo'}
                    </button>
                    {messages.length > 0 && (
                        <button className="btn btn-sm btn-secondary" onClick={clearChat}>
                            Limpar Chat
                        </button>
                    )}
                </div>
            </div>

            <div className="ai-chat-messages">
                {messages.length === 0 ? (
                    <div className="ai-chat-welcome">
                        <div className="ai-welcome-icon">{'\u2601\uFE0F'}</div>
                        <h3>Como posso ajudar?</h3>
                        <p>Envie comandos em linguagem natural para gerenciar seus domínios DNS no Cloudflare.</p>
                        <div className="ai-examples">
                            <p className="ai-examples-title">Exemplos:</p>
                            <ul>
                                <li>"Liste todos os domínios"</li>
                                <li>"Crie o registro www.exemplo.com.br apontando para 192.168.1.1"</li>
                                <li>"Quais registros apontam para ptbd01?"</li>
                                <li>"Verifique a propagação de exemplo.com.br"</li>
                                <li>"Altere o TTL de todos os registros de exemplo.com.br para 300"</li>
                            </ul>
                        </div>
                        <div className="ai-quick-actions">
                            {QUICK_ACTIONS.map((action, i) => (
                                <button
                                    key={i}
                                    className="ai-quick-btn"
                                    onClick={() => handleQuickAction(action)}
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {messages.map((msg, i) => (
                            <ChatMessage key={i} message={msg} />
                        ))}
                        {loading && (
                            <div className="chat-message chat-message-assistant">
                                <div className="chat-message-avatar">{'\uD83E\uDD16'}</div>
                                <div className="chat-message-content">
                                    <div className="chat-typing">
                                        <div className="typing-dot"></div>
                                        <div className="typing-dot"></div>
                                        <div className="typing-dot"></div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {error && (
                            <div className="chat-error">
                                <strong>Erro:</strong> {error}
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            <form className="ai-chat-input-area" onSubmit={handleSubmit}>
                <div className="ai-chat-input-wrapper">
                    <textarea
                        ref={inputRef}
                        className="ai-chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Digite um comando... (ex: liste todos os domínios)"
                        rows={1}
                        disabled={loading}
                    />
                    <button
                        type="submit"
                        className="ai-send-btn"
                        disabled={!input.trim() || loading}
                    >
                        {loading ? (
                            <span className="ai-send-loading"></span>
                        ) : (
                            '\u27A4'
                        )}
                    </button>
                </div>
                <p className="ai-chat-hint">
                    Enter para enviar {'\u00B7'} Shift+Enter para nova linha
                </p>
            </form>

            {showModelSelector && (
                <ModelSelector
                    currentModel={currentModel}
                    onModelChange={(model) => {
                        setCurrentModel(model);
                        setShowModelSelector(false);
                    }}
                    onClose={() => setShowModelSelector(false)}
                />
            )}
        </div>
    );
}

export default AIChat;
